import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, LayoutGrid, List } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { EXP_STATUS, EXP_TYPE_LABEL, EXP_TYPES } from "./eventosUtils";
import { ReadinessBadge } from "./Readiness";
import ExperienceDrawer from "./ExperienceDrawer";

const feil = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Noe gikk galt");

const Kort = ({ ex, spaceNavn, onOpen }) => {
  const [dras, setDras] = useState(false);
  return (
    <button
      type="button"
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("text/experience", ex.id); setDras(true); }}
      onDragEnd={() => setDras(false)}
      onClick={() => onOpen(ex.id)}
      className={`sak-kort w-full text-left bg-white border border-[#E7E4DB] rounded-[10px] px-3 py-2.5 ${dras ? "dras" : ""}`}
      data-testid={`experience-kort-${ex.id}`}
    >
      <p className="text-[13px] font-medium text-neutral-900 leading-snug">{ex.name}</p>
      <p className="text-[10.5px] text-neutral-400 mt-0.5 truncate">
        {[EXP_TYPE_LABEL[ex.type], ex.start && ex.end ? `${ex.start}–${ex.end}` : null, spaceNavn].filter(Boolean).join(" · ")}
      </p>
      <div className="flex items-center gap-2 mt-2">
        <ReadinessBadge computed={ex.computed} />
        {ex.openTasks > 0 && <span className="text-[10px] text-neutral-400 ml-auto">{ex.openTasks} saker</span>}
      </div>
    </button>
  );
};

export default function ExperiencesOS() {
  const [data, setData] = useState(null);
  const [visning, setVisning] = useState("tavle");
  const [aapen, setAapen] = useState(null);
  const [nyModal, setNyModal] = useState(false);
  const [nyNavn, setNyNavn] = useState("");
  const [nyType, setNyType] = useState("other");
  const [draOver, setDraOver] = useState(null);
  const aktor = localStorage.getItem("nas_aktor") || "Admin";

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/os/experiences");
      setData(d);
    } catch (e) {
      feil(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (eid, p) => {
    try {
      const { data: r } = await api.patch(`/admin/os/experiences/${eid}`, { ...p, actor: aktor });
      setData((prev) => ({
        ...prev,
        experiences: prev.experiences.map((x) => (x.id === eid ? { ...x, ...r.item, openTasks: x.openTasks } : x)),
      }));
    } catch (e) {
      feil(e);
    }
  };

  const opprett = async () => {
    const navn = nyNavn.trim();
    if (!navn) return;
    try {
      const { data: r } = await api.post("/admin/os/experiences", { name: navn, type: nyType, actor: aktor });
      setNyModal(false);
      setNyNavn("");
      toast.success("Experience opprettet");
      await load();
      setAapen(r.item.id);
    } catch (e) {
      feil(e);
    }
  };

  const slett = async (eid) => {
    if (!window.confirm("Slette experiencen?")) return;
    try {
      await api.delete(`/admin/os/experiences/${eid}`);
      setAapen(null);
      setData((prev) => ({ ...prev, experiences: prev.experiences.filter((x) => x.id !== eid) }));
      toast.success("Slettet");
    } catch (e) {
      feil(e);
    }
  };

  const aapenExp = useMemo(() => data?.experiences.find((x) => x.id === aapen) || null, [data, aapen]);
  const spaceNavn = useMemo(() => Object.fromEntries((data?.spaces || []).map((s) => [s.id, s.name])), [data]);

  if (!data) {
    return <p className="text-center text-xs text-neutral-400 py-20" data-testid="experiencesos-laster">Laster experiences …</p>;
  }

  const bekreftet = data.experiences.filter((x) => x.status === "confirmed").length;

  return (
    <div data-testid="experiencesos-admin">
      <header className="flex flex-wrap items-end gap-3 mb-5">
        <div className="mr-auto">
          <p className="adm-eyebrow mb-1.5">Event 2027</p>
          <h1 className="adm-title">Experience</h1>
          <p className="adm-sub mt-1.5" data-testid="experiencesos-teller">
            {bekreftet} bekreftet · {data.experiences.length} totalt — expo, middager, mottakelser og aktiveringer
          </p>
        </div>
        <div className="flex bg-white border border-[#E7E4DB] rounded-lg p-0.5">
          {[
            { k: "tavle", label: "Tavle", Ico: LayoutGrid },
            { k: "liste", label: "Liste", Ico: List },
          ].map(({ k, label, Ico }) => (
            <button
              key={k}
              type="button"
              onClick={() => setVisning(k)}
              className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1.5 rounded-md transition-all ${visning === k ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-800"}`}
              data-testid={`experiencesos-view-${k}`}
            >
              <Ico size={13} /> {label}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setNyModal(true)} className="adm-btn" data-testid="experiencesos-ny-btn">
          <Plus size={14} /> Ny experience
        </button>
      </header>

      {visning === "tavle" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="experiencesos-tavle">
          {EXP_STATUS.map((st) => {
            const gruppe = data.experiences.filter((x) => x.status === st.k);
            return (
              <div
                key={st.k}
                className={`rounded-xl bg-[#F1EFE8] px-2 pt-2 pb-3 min-h-[220px] transition-colors ${draOver === st.k ? "bg-[#ECEADE]" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDraOver(st.k); }}
                onDragLeave={() => setDraOver(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDraOver(null);
                  const id = e.dataTransfer.getData("text/experience");
                  const ex = data.experiences.find((x) => x.id === id);
                  if (ex && ex.status !== st.k) patch(id, { status: st.k });
                }}
                data-testid={`experiencesos-kolonne-${st.k}`}
              >
                <div className="flex items-center gap-1.5 px-1.5 py-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: st.dot }} />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-600">{st.label}</span>
                  <span className="text-[10.5px] tabular-nums text-neutral-400 ml-auto">{gruppe.length}</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  {gruppe.map((ex) => (
                    <Kort key={ex.id} ex={ex} spaceNavn={spaceNavn[ex.spaceId]} onOpen={setAapen} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {visning === "liste" && (
        <div className="adm-card overflow-hidden" data-testid="experiencesos-liste">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Navn</th>
                <th>Type</th>
                <th>Tid & rom</th>
                <th className="adm-right">Kapasitet</th>
                <th>Status</th>
                <th>Readiness</th>
                <th className="adm-right">Åpne saker</th>
              </tr>
            </thead>
            <tbody>
              {data.experiences.map((ex) => {
                const st = EXP_STATUS.find((s) => s.k === ex.status) || EXP_STATUS[0];
                return (
                  <tr key={ex.id} onClick={() => setAapen(ex.id)} className="cursor-pointer" data-testid={`experiencesos-rad-${ex.id}`}>
                    <td className="font-medium">{ex.name}</td>
                    <td className="text-neutral-500">{EXP_TYPE_LABEL[ex.type]}</td>
                    <td className="text-neutral-500 text-[12px]">
                      {[ex.start && ex.end ? `${ex.start}–${ex.end}` : null, spaceNavn[ex.spaceId]].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="text-right tabular-nums text-neutral-500">{ex.capacity || "—"}</td>
                    <td>
                      <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium" style={{ color: st.dot }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.dot }} />
                        {st.label}
                      </span>
                    </td>
                    <td><ReadinessBadge computed={ex.computed} /></td>
                    <td className="text-right tabular-nums text-neutral-500">{ex.openTasks || "—"}</td>
                  </tr>
                );
              })}
              {data.experiences.length === 0 && (
                <tr><td colSpan={7} className="text-center text-xs text-neutral-400 py-6">Ingen experiences ennå.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {aapenExp && (
        <ExperienceDrawer
          exp={aapenExp}
          spaces={data.spaces}
          sponsors={data.sponsors}
          budgetLines={data.budgetLines}
          members={data.members}
          aktor={aktor}
          onPatch={(p) => patch(aapenExp.id, p)}
          onReload={load}
          onDelete={() => slett(aapenExp.id)}
          onLukk={() => setAapen(null)}
        />
      )}

      {nyModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4" data-testid="ny-experience-modal">
          <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={() => setNyModal(false)} />
          <div className="sak-modal relative w-full max-w-[420px] bg-white rounded-2xl shadow-2xl border border-[#E7E4DB] p-6">
            <h2 className="text-[15px] font-semibold mb-4">Ny experience</h2>
            <label className="adm-label">
              Navn *
              <input
                value={nyNavn}
                onChange={(e) => setNyNavn(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); opprett(); } }}
                className="adm-input"
                autoFocus
                data-testid="ny-experience-navn-input"
              />
            </label>
            <label className="adm-label mt-3">
              Type
              <select value={nyType} onChange={(e) => setNyType(e.target.value)} className="adm-input" data-testid="ny-experience-type-select">
                {EXP_TYPES.map((t) => (
                  <option key={t.k} value={t.k}>{t.label}</option>
                ))}
              </select>
            </label>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button type="button" onClick={() => setNyModal(false)} className="adm-btn-ghost">Avbryt</button>
              <button type="button" onClick={opprett} disabled={!nyNavn.trim()} className="adm-btn" data-testid="ny-experience-lagre-btn">
                Opprett
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
